/**
 * Component for editing quiz totals with drag-and-drop reordering.
 *
 * Totals are shown on the results page and display content based on score ranges.
 *
 * @module components/quizzes/edit-quiz-totals
 */

import type { Quiz, QuizTotal } from '@efdigital/ll-addon-data';
import { DataTypeQuiz } from '@efdigital/ll-addon-data/data-types';
import { debounce } from '@efdigital/ll-addon-ui/modifiers/debounce';
import {
  addClass,
  hasClass,
  removeClass,
} from '@efdigital/ll-addon-ui/utils/css-class-helpers';
import { clone } from '@efdigital/ll-admin-v2/utils/array.ts';
import { fn } from '@ember/helper';
import { uniqueId } from '@ember/helper';
import { on } from '@ember/modifier';
import { next } from '@ember/runloop';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Type } from '@warp-drive/core/types/symbols';

import { DragToSort } from '../drag-to-sort.gts';
import { type EditRecordSignature } from '../edit-record-base.gts';
import { EditRecordCollapse } from '../edit-record-collapse.gts';
import TotalEditorButtons from './edit-quiz-totals/total-editor-buttons.gts';
import TotalEditorCards from './edit-quiz-totals/total-editor-cards.gts';
import { addOne, eq, type TotalEditingMode } from './edit-quiz-totals/utils.ts';

export class EditQuizTotals extends Component<EditRecordSignature<Quiz>> {
  @tracked selectedIndex = -1;
  @tracked editingMode: TotalEditingMode = 'inactive';

  readonly uniqueId = uniqueId();

  get showScore(): boolean {
    return !hasClass(this.args.item.css ?? '', 'hide-score');
  }

  toggleShowScore = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.item.css = target.checked
      ? addClass(this.args.item.css ?? '', 'hide-score')
      : removeClass(this.args.item.css ?? '', 'hide-score');
  };

  onEdit = () => {
    this.selectedIndex = -1;
    this.editingMode = 'inactive';
  };

  /**
   * Add a new total to the quiz.
   */
  addTotal = () => {
    const newTotal: QuizTotal = {
      [Type]: DataTypeQuiz.QuizTotal,
      from: 0,
      to: 100,
      qid: '',
      answer: '',
      category: '',
      response: {
        [Type]: DataTypeQuiz.QuizResponse,
        title: '<Enter heading>',
        markdown: '',
        css: '',
        embed: '',
        selection: '',
        readinglists: [],
        resources: [],
        topics: [],
        goals: [],
        route: '',
      },
    };

    this.args.item.totals = [...this.args.item.totals, newTotal];
    this.selectedIndex = this.args.item.totals.length - 1;
    this.editingMode = 'inactive';

    // TODO: Remove this once we have a better way to scroll to the total
    // replacing `next` with `runTask` from ember-lifeline
    /* eslint-disable-next-line ember/no-runloop */
    next(() => {
      const element = document.getElementById(
        `${this.uniqueId}-total-title-${this.args.item.totals.length - 1}`
      ) as HTMLInputElement;
      if (element) {
        element.focus();
        element.select();
      }
    });

    const total = this.args.item.totals[this.selectedIndex];
    if (total) {
      this.scrollToTotal(this.selectedIndex);
    }
  };

  /**
   * Remove a total from the quiz.
   */
  removeTotalAt = (totals: QuizTotal[], index: number) => {
    const copy = [...totals];
    copy.splice(index, 1);
    totals.splice(0, totals.length, ...copy.map(clone));

    this.selectedIndex = -1;
    this.editingMode = 'inactive';

    const scrollToIndex = index === 0 ? 0 : index - 1;
    if (scrollToIndex < totals.length) {
      this.scrollToTotal(scrollToIndex);
    }
  };

  /**
   * Update the totals array after reordering.
   */
  updateTotals = (totals: QuizTotal[]) => {
    this.args.item.totals.splice(0, totals.length, ...totals.map(clone));
    this.selectedIndex = -1;
    this.editingMode = 'inactive';
  };

  setTotalTitle = (total: QuizTotal, event: Event) => {
    const target = event.target as HTMLInputElement;
    total.response.title = target.value;
  };

  setEditingMode = (
    total: QuizTotal | null,
    index: number,
    mode: TotalEditingMode
  ) => {
    if (total) {
      this.scrollToTotal(index);
    }
    this.selectedIndex = index;
    this.editingMode = mode;
  };

  isEditing = (index: number) => {
    return this.selectedIndex === index;
  };

  isEditingMode = (index: number, mode: TotalEditingMode) => {
    return this.selectedIndex === index && this.editingMode === mode;
  };

  scrollToTotal = (index: number) => {
    // TODO: Remove this once we have a better way to scroll to the total
    // replacing `next` with `runTask` from ember-lifeline
    /* eslint-disable-next-line ember/no-runloop */
    next(() => {
      const totalElement = document.getElementById(
        `${this.uniqueId}-total-${index}`
      );
      if (totalElement) {
        totalElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    });
  };

  categoryFor = (cid: string) => {
    return this.args.item.categories.find((c) => c.cid === cid)?.title ?? '';
  };

  questionFor = (qid: string) => {
    return this.args.item.questions.find((q) => q.qid === qid)?.title ?? '';
  };

  hasScoringScope = (total: QuizTotal) => {
    return total.from || total.to;
  };

  <template>
    <EditRecordCollapse
      @editLabel="Edit Result Interpretations"
      @item={{@item}}
      @context={{@context}}
      @onEdit={{this.onEdit}}
      @maxDepth={{1}}
      @collapseLabel="Result Interpretations"
      ...attributes
    >
      <:help>
        <p class="form-text">
          Interpretations provide personalized content based on score ranges -
          analyzing performance and offering guidance tailored to each result.
        </p>
      </:help>
      <:form as |uniqueId isDisabled isReadOnly|>
        <fieldset disabled={{isDisabled}} class="mb-4">
          <div class="form-check mb-3">
            <input
              type="checkbox"
              class="form-check-input"
              id="{{uniqueId}}-show-score"
              checked={{this.showScore}}
              {{on "change" this.toggleShowScore}}
            />
            <label class="form-check-label" for="{{uniqueId}}-show-score">
              Show total score on results page
            </label>
            <div class="form-text">
              Display the grand total score to participants after submission.
            </div>
          </div>

          {{#if @item.totals.length}}
            {{#if isReadOnly}}
              <div class="list-group mb-3">
                {{#each @item.totals as |total|}}
                  <div class="list-group-item">
                    <strong>{{total.response.title}}</strong>
                    {{#if (this.hasScoringScope total)}}
                      <div class="small">
                        <span class="text-muted">Scoring {{total.from}}</span>
                        –
                        {{total.to}}
                        {{#if total.category}}
                          <span class="text-muted">in</span>
                          <strong>{{this.categoryFor total.category}}</strong>
                        {{/if}}
                        {{#if total.qid}}
                          <span class="text-muted">for</span>
                          <em>{{this.questionFor total.qid}}</em>
                        {{/if}}
                      </div>
                    {{/if}}
                  </div>
                {{/each}}
              </div>
            {{else}}
              <div class="list-group mb-3 gap-1">
                <DragToSort
                  @items={{@item.totals}}
                  @updateItems={{this.updateTotals}}
                >
                  <:item as |dropZone draggable total index|>
                    {{#let (eq this.selectedIndex index) as |isEditing|}}
                      <div
                        class="question-item-container
                          {{if isEditing 'is-editing shadow rounded-bottom-1'}}"
                        id="{{this.uniqueId}}-total-{{index}}"
                        tabindex="0"
                      >
                        <div
                          class="d-flex flex-row align-items-stretch position-relative"
                          {{dropZone index}}
                        >
                          <span
                            class="input-group-text justify-content-center drag-handle w-3rem rounded-end-0 border-end-0 d-flex flex-column align-items-center
                              {{if isEditing 'rounded-bottom-0'}}"
                            title="Drag to reorder"
                            {{draggable total index}}
                          >
                            <iconify-icon
                              inline
                              icon="ph:sigma"
                              class="my-1 fs-4"
                            />
                            <div class="text-center mb-auto">
                              {{addOne index}}
                            </div>
                          </span>

                          <div class="d-flex flex-column flex-grow-1">
                            <input
                              id="{{this.uniqueId}}-total-title-{{index}}"
                              type="text"
                              class="form-control list-group-item rounded-start-0 rounded-bottom-0 rounded-top-1"
                              value={{total.response.title}}
                              required
                              {{debounce (fn this.setTotalTitle total)}}
                              {{on
                                "focus"
                                (fn
                                  this.setEditingMode
                                  total
                                  index
                                  (if isEditing this.editingMode "inactive")
                                )
                              }}
                            />

                            <TotalEditorButtons
                              @total={{total}}
                              @index={{index}}
                              @isEditing={{isEditing}}
                              @isEditingMode={{this.isEditingMode}}
                              @setEditingMode={{this.setEditingMode}}
                              @onRemove={{fn
                                this.removeTotalAt
                                @item.totals
                                index
                              }}
                            />
                          </div>
                        </div>

                        <TotalEditorCards
                          @total={{total}}
                          @index={{index}}
                          @quiz={{@item}}
                          @isEditingMode={{this.isEditingMode}}
                        />
                      </div>
                    {{/let}}
                  </:item>
                </DragToSort>
              </div>
            {{/if}}
          {{else}}
            <div class="alert alert-info small">
              No result interpretations have been created yet.
            </div>
          {{/if}}
        </fieldset>
      </:form>
      <:buttons as |uniqueId isReadOnly|>
        {{#unless isReadOnly}}
          <button
            type="button"
            class="btn btn-primary btn-sm"
            id="{{uniqueId}}-add-total"
            {{on "click" this.addTotal}}
          >
            <iconify-icon inline icon="ph:plus" />
            Add Interpretation
          </button>
        {{/unless}}
      </:buttons>
    </EditRecordCollapse>
  </template>
}
